/**
 * UI-only helpers for project team picker.
 * Backend stores linkedTeamIds and memberUserIds independently — no server merge.
 */

/**
 * @param {string[]} ids
 * @returns {string[]}
 */
export function dedupeMemberIds(ids = []) {
  return [...new Set((ids || []).map(String).filter(Boolean))];
}

/**
 * Suggest members when OMIND teams are newly added in the dialog.
 * Does not remove anyone when teams are deselected.
 *
 * @param {string[]} newlyAddedTeamIds
 * @param {Record<string, string[]>} teamUserMap - teamId → userId[]
 * @param {string[]} currentMemberIds
 * @returns {string[]}
 */
export function suggestMembersFromTeams(
  newlyAddedTeamIds = [],
  teamUserMap = {},
  currentMemberIds = [],
) {
  const suggested = [];
  for (const teamId of newlyAddedTeamIds || []) {
    const users = teamUserMap[String(teamId)] || [];
    suggested.push(...users.map(String));
  }
  return dedupeMemberIds([...currentMemberIds, ...suggested]);
}

/**
 * Build teamId → userId[] map from OMIND teams that carry a members array.
 *
 * @param {Array<{ _id?: string, id?: string, members?: string[] }>} teams
 * @returns {Record<string, string[]>}
 */
export function buildTeamUserMapFromTeams(teams = []) {
  const map = {};
  for (const team of teams || []) {
    const teamId = String(team._id || team.id || '').trim();
    if (!teamId) continue;
    const members = Array.isArray(team.members)
      ? team.members.map(String).filter(Boolean)
      : [];
    map[teamId] = [...new Set(members)];
  }
  return map;
}

/**
 * Build teamId → userId[] map from participants that carry a teams array.
 *
 * @param {Array<{ id: string, teams?: string[] }>} users
 * @returns {Record<string, string[]>}
 */
export function buildTeamUserMap(users = []) {
  const map = {};
  for (const user of users || []) {
    const userId = String(user.id || '').trim();
    if (!userId) continue;
    const teams = Array.isArray(user.teams) ? user.teams.map(String) : [];
    for (const teamId of teams) {
      if (!map[teamId]) map[teamId] = [];
      if (!map[teamId].includes(userId)) map[teamId].push(userId);
    }
  }
  return map;
}
